// frontend/src/components/dedup/DeduplicationProgress.tsx
import { getDedupUrl, authFetch } from '../../config';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';

interface Props {
  campaignId: number;
  sources: any[];
  rule: any;
  status: 'idle' | 'running' | 'completed' | 'error';
  onStatusChange: (status: 'idle' | 'running' | 'completed' | 'error') => void;
}

interface ProgressData {
  current: number;
  total: number;
  phase: string;
  duplicates_found: number;
  unique_records: number;
  errors: number;
}

export default function DeduplicationProgress({ 
  campaignId, 
  sources, 
  rule, 
  status, 
  onStatusChange 
}: Props) {
  const [progress, setProgress] = useState<ProgressData>({
    current: 0,
    total: 0,
    phase: 'idle',
    duplicates_found: 0,
    unique_records: 0,
    errors: 0
  });
  const [logs, setLogs] = useState<string[]>([]);

  useEffect(() => {
    if (status === 'running') {
      pollProgress();
    }
  }, [status]);

  const pollProgress = async () => {
    const interval = setInterval(async () => {
      try {
        const response = await authFetch(
          getDedupUrl(`/campaigns/${campaignId}/progress`)
        );
        const result = await response.json();
        
        if (result.success) {
          setProgress(result.progress);
          if (result.logs) {
            setLogs(prev => [...prev, ...result.logs]);
          }

          if (result.progress.phase === 'completed') {
            clearInterval(interval);
            onStatusChange('completed');
          } else if (result.progress.phase === 'error') {
            clearInterval(interval);
            onStatusChange('error');
          }
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 1000);

    return () => clearInterval(interval);
  };

  const startDeduplication = async () => {
    onStatusChange('running');
    setLogs(['Deduplizierung gestartet...']);

    try {
      const response = await authFetch(
        getDedupUrl(`/campaigns/${campaignId}/execute`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ rule })
        }
      );

      const result = await response.json();
      
      if (!result.success) {
        onStatusChange('error');
        setLogs(prev => [...prev, `Fehler: ${result.message}`]);
      }
    } catch (error) {
      onStatusChange('error');
      setLogs(prev => [...prev, `Fehler: ${error}`]);
    }
  };

  const getProgressPercentage = () => {
    if (progress.total === 0) return 0;
    return Math.round((progress.current / progress.total) * 100);
  };

  const getPhaseLabel = (phase: string) => {
    const labels: Record<string, string> = {
      idle: 'Bereit',
      loading: 'Lade Daten...',
      matching: 'Suche Duplikate...',
      resolving: 'Löse Prioritäten auf...',
      writing: 'Schreibe Ergebnisse...',
      completed: 'Abgeschlossen',
      error: 'Fehler'
    };
    return labels[phase] || phase;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Deduplizierung durchführen
      </Typography>

      {status === 'idle' && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Bereit zur Deduplizierung mit folgender Konfiguration:
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                • <strong>{sources.length}</strong> Quellen
              </Typography>
              <Typography variant="body2">
                • <strong>{rule.match_fields.length}</strong> Match-Kriterien
              </Typography>
              <Typography variant="body2">
                • Schwellenwert: <strong>{rule.match_threshold}%</strong>
              </Typography>
            </Box>
          </Alert>

          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayIcon />}
              onClick={startDeduplication}
            >
              Deduplizierung starten
            </Button>
          </Box>
        </>
      )}

      {(status === 'running' || status === 'completed' || status === 'error') && (
        <>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 3 
          }}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {progress.duplicates_found}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Duplikate gefunden
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {progress.unique_records}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Einzigartige Datensätze
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4">
                {progress.current}/{progress.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Verarbeitet
              </Typography>
            </Paper>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" color={progress.errors > 0 ? 'error.main' : 'text.primary'}>
                {progress.errors}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fehler
              </Typography>
            </Paper>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" gutterBottom>
                    {getPhaseLabel(progress.phase)}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={getProgressPercentage()} 
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Typography variant="body2" sx={{ ml: 2, minWidth: 60, textAlign: 'right' }}>
                  {getProgressPercentage()}%
                </Typography>
              </Box>

              {status === 'completed' && (
                <Alert severity="success" icon={<CheckIcon />}>
                  <Typography variant="body2">
                    Deduplizierung erfolgreich abgeschlossen!
                  </Typography>
                </Alert>
              )}

              {status === 'error' && (
                <Alert severity="error" icon={<ErrorIcon />}>
                  <Typography variant="body2">
                    Bei der Deduplizierung ist ein Fehler aufgetreten. 
                    Bitte überprüfen Sie die Logs.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Verarbeitungs-Log
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  maxHeight: 300, 
                  overflow: 'auto', 
                  bgcolor: 'grey.50',
                  p: 2
                }}
              >
                <List dense>
                  {logs.map((log, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
                      <ListItemText 
                        primary={log}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          fontFamily: 'monospace'
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}